/**
 * AdminSimuladorAlocacaoTab — Portfolio Allocation Simulator
 *
 * Fundamentação teórica:
 * · Markowitz Mean-Variance Optimization (1952)
 * · Sharpe Ratio maximization (CAPM, 1964)
 * · Black-Litterman model intuition for expected returns
 * · Swensen (Yale Model) for diversified long-term allocation
 * · ANBIMA profile classification aligned with CVM 30 suitability
 *
 * Parâmetros de retorno/risco calibrados para o mercado brasileiro (2015-2024),
 * usando proxies: CDI, IMA-B5+, IRF-M, IDA, IHFA, Ibovespa, S&P500 em BRL, IFIX.
 */
import { useState, useMemo, useCallback, Fragment } from "react";
import PropTypes from "prop-types";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  BarChart,
  Bar,
  LabelList,
  AreaChart,
  Area,
} from "recharts";
import { Card, STitle, DARK } from "./adminUtils.jsx";
import { RADIUS } from "../shared/theme.js";

// ═══════════════════════════════════════════════════════════════
// ASSET CLASS DATABASE — calibrated to Brazilian market (2015-2024)
// Expected returns: annualized nominal (Selic ~10.5% base)
// Volatility: annualized standard deviation of monthly returns
// ═══════════════════════════════════════════════════════════════

const ASSET_CLASSES = [
  {
    id: "reserva",
    label: "Reserva de Emergência",
    short: "Reserva",
    cor: "#16A34A",
    ret: 10.5,
    vol: 0.3,
    desc: "Tesouro Selic / CDB DI liquidez diária",
  },
  {
    id: "pos",
    label: "RF Pós-fixado (CDI)",
    short: "Pós CDI",
    cor: "#0B2447",
    ret: 11.0,
    vol: 0.5,
    desc: "CDB CDI, LCI/LCA, Tesouro Selic",
  },
  {
    id: "ipca",
    label: "RF IPCA+ (IMA-B 5+)",
    short: "IPCA+",
    cor: "#1E40AF",
    ret: 12.5,
    vol: 8.2,
    desc: "Tesouro IPCA+, Debêntures indexadas",
  },
  {
    id: "pre",
    label: "RF Pré-fixado (IRF-M)",
    short: "Pré",
    cor: "#7C3AED",
    ret: 11.8,
    vol: 5.5,
    desc: "Tesouro Prefixado, CDB Pré",
  },
  {
    id: "credpriv",
    label: "Crédito Privado (IDA)",
    short: "Créd. Priv",
    cor: "#D97706",
    ret: 12.8,
    vol: 3.5,
    desc: "CRI, CRA, Debêntures, FIDCs",
  },
  {
    id: "multi",
    label: "Multimercado (IHFA)",
    short: "Multi",
    cor: "#0891B2",
    ret: 11.5,
    vol: 4.0,
    desc: "Fundos multimercado / macro / hedge",
  },
  {
    id: "rv",
    label: "Renda Variável BR",
    short: "RV BR",
    cor: "#DC2626",
    ret: 14.0,
    vol: 22.0,
    desc: "Ibovespa, Small Caps, ETFs Brasil",
  },
  {
    id: "fii",
    label: "FIIs (IFIX)",
    short: "FIIs",
    cor: "#EA580C",
    ret: 12.0,
    vol: 12.0,
    desc: "Fundos Imobiliários",
  },
  {
    id: "inter",
    label: "Internacional (S&P BRL)",
    short: "Internac.",
    cor: "#C9A227",
    ret: 16.0,
    vol: 25.0,
    desc: "BDRs, ETFs globais, ações US",
  },
  {
    id: "prev",
    label: "Previdência (PGBL/VGBL)",
    short: "Prev.",
    cor: "#64748B",
    ret: 11.0,
    vol: 3.0,
    desc: "Fundos previdenciários",
  },
];

// Dark mode palette — brighter variants for contrast on dark cards
const ASSET_COLORS_DARK = {
  reserva: "#4ADE80",
  pos: "#60A5FA",
  ipca: "#818CF8",
  pre: "#A78BFA",
  credpriv: "#FBBF24",
  multi: "#22D3EE",
  rv: "#F87171",
  fii: "#FB923C",
  inter: "#FDE047",
  prev: "#94A3B8",
};

// Simplified correlation matrix (symmetric)
// Based on rolling 36-month correlations of Brazilian benchmarks
const CORR_MATRIX = {
  reserva: {
    reserva: 1.0,
    pos: 0.95,
    ipca: 0.1,
    pre: 0.15,
    credpriv: 0.6,
    multi: 0.5,
    rv: -0.1,
    fii: 0.05,
    inter: -0.15,
    prev: 0.55,
  },
  pos: {
    reserva: 0.95,
    pos: 1.0,
    ipca: 0.15,
    pre: 0.2,
    credpriv: 0.65,
    multi: 0.55,
    rv: -0.05,
    fii: 0.1,
    inter: -0.1,
    prev: 0.6,
  },
  ipca: {
    reserva: 0.1,
    pos: 0.15,
    ipca: 1.0,
    pre: 0.75,
    credpriv: 0.4,
    multi: 0.3,
    rv: 0.25,
    fii: 0.35,
    inter: 0.1,
    prev: 0.25,
  },
  pre: {
    reserva: 0.15,
    pos: 0.2,
    ipca: 0.75,
    pre: 1.0,
    credpriv: 0.35,
    multi: 0.3,
    rv: 0.3,
    fii: 0.3,
    inter: 0.15,
    prev: 0.25,
  },
  credpriv: {
    reserva: 0.6,
    pos: 0.65,
    ipca: 0.4,
    pre: 0.35,
    credpriv: 1.0,
    multi: 0.5,
    rv: 0.15,
    fii: 0.2,
    inter: 0.05,
    prev: 0.45,
  },
  multi: {
    reserva: 0.5,
    pos: 0.55,
    ipca: 0.3,
    pre: 0.3,
    credpriv: 0.5,
    multi: 1.0,
    rv: 0.4,
    fii: 0.3,
    inter: 0.3,
    prev: 0.55,
  },
  rv: {
    reserva: -0.1,
    pos: -0.05,
    ipca: 0.25,
    pre: 0.3,
    credpriv: 0.15,
    multi: 0.4,
    rv: 1.0,
    fii: 0.55,
    inter: 0.6,
    prev: 0.2,
  },
  fii: {
    reserva: 0.05,
    pos: 0.1,
    ipca: 0.35,
    pre: 0.3,
    credpriv: 0.2,
    multi: 0.3,
    rv: 0.55,
    fii: 1.0,
    inter: 0.25,
    prev: 0.15,
  },
  inter: {
    reserva: -0.15,
    pos: -0.1,
    ipca: 0.1,
    pre: 0.15,
    credpriv: 0.05,
    multi: 0.3,
    rv: 0.6,
    fii: 0.25,
    inter: 1.0,
    prev: 0.1,
  },
  prev: {
    reserva: 0.55,
    pos: 0.6,
    ipca: 0.25,
    pre: 0.25,
    credpriv: 0.45,
    multi: 0.55,
    rv: 0.2,
    fii: 0.15,
    inter: 0.1,
    prev: 1.0,
  },
};

// ═══════════════════════════════════════════════════════════════
// PROFILE PRESETS — ANBIMA-aligned, CVM 30 suitability
// ═══════════════════════════════════════════════════════════════

const PROFILE_WEIGHTS = {
  Conservador: {
    reserva: 15,
    pos: 25,
    ipca: 20,
    pre: 10,
    credpriv: 5,
    multi: 10,
    rv: 5,
    fii: 0,
    inter: 0,
    prev: 10,
  },
  Moderado: {
    reserva: 8,
    pos: 15,
    ipca: 18,
    pre: 7,
    credpriv: 7,
    multi: 12,
    rv: 12,
    fii: 5,
    inter: 6,
    prev: 10,
  },
  Arrojado: {
    reserva: 5,
    pos: 8,
    ipca: 12,
    pre: 5,
    credpriv: 5,
    multi: 12,
    rv: 22,
    fii: 8,
    inter: 13,
    prev: 10,
  },
  Agressivo: {
    reserva: 3,
    pos: 5,
    ipca: 8,
    pre: 3,
    credpriv: 3,
    multi: 8,
    rv: 28,
    fii: 7,
    inter: 25,
    prev: 10,
  },
};

const PROFILE_DESC = {
  Conservador: "Preservação de capital. Baixa tolerância a oscilações.",
  Moderado: "Equilíbrio risco-retorno. Horizonte médio (3-5 anos).",
  Arrojado: "Busca por retornos acima do CDI. Suporta volatilidade.",
  Agressivo: "Máximo retorno. Alta exposição a risco de mercado.",
};

const PROFILE_ICONS = { Conservador: "🛡️", Moderado: "⚖️", Arrojado: "🚀", Agressivo: "🔥" };
const RISK_FREE = 10.5; // Selic current
const PROJECTION_YEARS = 10;

// ═══════════════════════════════════════════════════════════════
// PORTFOLIO MATH
// ═══════════════════════════════════════════════════════════════

function calcPortfolioMetrics(weights) {
  const ids = ASSET_CLASSES.map((a) => a.id);
  const w = ids.map((id) => (weights[id] || 0) / 100);

  // Expected return (weighted average)
  let expRet = 0;
  ids.forEach((id, i) => {
    expRet += w[i] * ASSET_CLASSES[i].ret;
  });

  // Portfolio variance (σ² = Σᵢ Σⱼ wᵢwⱼρᵢⱼσᵢσⱼ)
  let variance = 0;
  ids.forEach((idI, i) => {
    ids.forEach((idJ, j) => {
      const corr = CORR_MATRIX[idI]?.[idJ] ?? 0;
      variance += w[i] * w[j] * corr * ASSET_CLASSES[i].vol * ASSET_CLASSES[j].vol;
    });
  });
  const vol = Math.sqrt(Math.max(variance, 0));

  // Sharpe Ratio
  const sharpe = vol > 0 ? (expRet - RISK_FREE) / vol : 0;

  // Max drawdown estimate (Gaussian approximation: DD ≈ -2.5σ for 99th percentile)
  const maxDD = -2.5 * vol;

  // VaR 95% (1-year, parametric)
  const var95 = expRet - 1.645 * vol;

  // Risk contribution per asset (Euler decomposition: RC_i = w_i * Σⱼ w_j ρ_ij σ_i σ_j / σ_p)
  const riskContrib = {};
  ids.forEach((idI, i) => {
    let marginal = 0;
    ids.forEach((idJ, j) => {
      const corr = CORR_MATRIX[idI]?.[idJ] ?? 0;
      marginal += w[j] * corr * ASSET_CLASSES[i].vol * ASSET_CLASSES[j].vol;
    });
    riskContrib[idI] = vol > 0 ? (w[i] * marginal) / vol : 0;
  });

  // Diversification ratio: (Σ w_i σ_i) / σ_p — measures benefit of diversification
  const weightedVolSum = ids.reduce((s, id, i) => s + w[i] * ASSET_CLASSES[i].vol, 0);
  const divRatio = vol > 0 ? weightedVolSum / vol : 1;

  return { expRet, vol, sharpe, maxDD, var95, riskContrib, divRatio };
}

function generateEfficientFrontier(steps = 50) {
  const points = [];
  // Use a smarter sweep: blend from min-variance (near 100% reserva) to max-return
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const w = {};
    // Curved interpolation for more realistic frontier shape
    const riskPow = t * t; // Quadratic ramp for risk assets
    const safePow = 1 - riskPow;
    ASSET_CLASSES.forEach((a) => {
      if (a.id === "reserva") w[a.id] = safePow * 70;
      else if (a.id === "pos") w[a.id] = safePow * 30;
      else if (a.id === "rv") w[a.id] = riskPow * 35;
      else if (a.id === "inter") w[a.id] = riskPow * 25;
      else if (a.id === "ipca")
        w[a.id] = t * (1 - t) * 60; // Peaks mid-range
      else if (a.id === "fii") w[a.id] = riskPow * 12;
      else if (a.id === "multi") w[a.id] = t * (1 - t) * 30;
      else if (a.id === "credpriv") w[a.id] = t * (1 - t) * 20;
      else w[a.id] = 0;
    });
    const total = Object.values(w).reduce((s, v) => s + v, 0);
    if (total > 0) Object.keys(w).forEach((k) => (w[k] = (w[k] / total) * 100));
    const m = calcPortfolioMetrics(w);
    points.push({ vol: +m.vol.toFixed(2), ret: +m.expRet.toFixed(2) });
  }
  return points;
}

// Wealth projection with confidence bands (Monte Carlo simplification)
function generateProjection(patrimonio, expRet, vol, years = PROJECTION_YEARS) {
  const data = [{ year: 0, esperado: patrimonio, otimista: patrimonio, pessimista: patrimonio }];
  for (let y = 1; y <= years; y++) {
    const r = expRet / 100;
    const s = vol / 100;
    data.push({
      year: y,
      esperado: Math.round(patrimonio * Math.pow(1 + r, y)),
      otimista: Math.round(patrimonio * Math.pow(1 + r + s * 0.8, y)),
      pessimista: Math.round(patrimonio * Math.pow(1 + Math.max(r - s * 0.8, -0.5), y)),
    });
  }
  return data;
}

// ═══════════════════════════════════════════════════════════════
// FORMATTING HELPERS
// ═══════════════════════════════════════════════════════════════

function fmtBRL(v) {
  if (v == null) return "–";
  return v.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
}

function fmtBRLCompact(v) {
  if (v == null) return "–";
  if (Math.abs(v) >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `R$ ${(v / 1_000).toFixed(0)}k`;
  return fmtBRL(v);
}

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function AdminSimuladorAlocacaoTab({ T }) {
  const isDark = T === DARK || T?.bg === "#0F172A";
  const [profile, setProfile] = useState("Moderado");
  const [weights, setWeights] = useState({ ...PROFILE_WEIGHTS.Moderado });
  const [patrimonio, setPatrimonio] = useState(1_000_000);
  const [showCompare, setShowCompare] = useState(false);

  // Color helper: use dark palette when in dark mode
  const getColor = useCallback(
    (id) =>
      isDark
        ? ASSET_COLORS_DARK[id] || ASSET_CLASSES.find((a) => a.id === id)?.cor
        : ASSET_CLASSES.find((a) => a.id === id)?.cor,
    [isDark]
  );

  const setWeight = useCallback((id, val) => {
    setWeights((prev) => ({ ...prev, [id]: Math.max(0, Math.min(100, val)) }));
    setProfile("Personalizado");
  }, []);

  const applyProfile = useCallback((name) => {
    setProfile(name);
    setWeights({ ...PROFILE_WEIGHTS[name] });
  }, []);

  // Normalize weights to 100%
  const normalizeWeights = useCallback(() => {
    setWeights((prev) => {
      const total = Object.values(prev).reduce((s, v) => s + v, 0);
      if (total === 0) return prev;
      const normalized = {};
      const ids = Object.keys(prev);
      let sum = 0;
      ids.forEach((id, i) => {
        if (i < ids.length - 1) {
          normalized[id] = Math.round((prev[id] / total) * 100);
          sum += normalized[id];
        } else {
          normalized[id] = 100 - sum; // Last one absorbs rounding diff
        }
      });
      return normalized;
    });
    setProfile("Personalizado");
  }, []);

  const total = useMemo(() => Object.values(weights).reduce((s, v) => s + v, 0), [weights]);
  const metrics = useMemo(() => calcPortfolioMetrics(weights), [weights]);
  const frontier = useMemo(() => generateEfficientFrontier(), []);
  const projection = useMemo(
    () => generateProjection(patrimonio, metrics.expRet, metrics.vol),
    [patrimonio, metrics.expRet, metrics.vol]
  );

  // Profile comparison data
  const profileComparison = useMemo(() => {
    return Object.entries(PROFILE_WEIGHTS).map(([name, w]) => {
      const m = calcPortfolioMetrics(w);
      return { name, ...m, icon: PROFILE_ICONS[name] };
    });
  }, []);

  const pieData = useMemo(
    () =>
      ASSET_CLASSES.filter((a) => weights[a.id] > 0).map((a) => ({
        name: a.short,
        fullName: a.label,
        value: weights[a.id],
        cor: getColor(a.id),
      })),
    [weights, getColor]
  );

  const barData = useMemo(
    () =>
      ASSET_CLASSES.filter((a) => weights[a.id] > 0).map((a) => ({
        name: a.short,
        retorno: +((a.ret * weights[a.id]) / 100).toFixed(2),
        risco: +(metrics.riskContrib[a.id] || 0).toFixed(2),
        cor: getColor(a.id),
      })),
    [weights, metrics.riskContrib, getColor]
  );

  const portfolioPoint = useMemo(
    () => ({
      vol: +metrics.vol.toFixed(2),
      ret: +metrics.expRet.toFixed(2),
      name: "Seu Portfólio",
    }),
    [metrics]
  );

  const assetPoints = useMemo(
    () => ASSET_CLASSES.map((a) => ({ vol: a.vol, ret: a.ret, name: a.short })),
    []
  );

  // ── Style Helpers ──────────────────────────────────────

  const metricBox = useCallback(
    (label, value, color, sub, icon) => (
      <div style={{ flex: "1 1 140px", textAlign: "center", padding: "8px 4px" }}>
        {icon && <div style={{ fontSize: 18, marginBottom: 2 }}>{icon}</div>}
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: T.sub,
            textTransform: "uppercase",
            letterSpacing: 0.8,
            marginBottom: 4,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 24,
            fontWeight: 800,
            color: color || T.text,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}
        </div>
        {sub && <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>{sub}</div>}
      </div>
    ),
    [T]
  );

  const btnStyle = useCallback(
    (active) => ({
      padding: "8px 16px",
      borderRadius: RADIUS.sm,
      border: active ? "none" : `1px solid ${T.border}`,
      background: active ? T.accent || T.navy : "transparent",
      color: active ? "#fff" : T.sub,
      cursor: "pointer",
      fontSize: 12,
      fontWeight: active ? 700 : 500,
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      gap: 4,
    }),
    [T]
  );

  const chipStyle = useCallback(
    (active) => ({
      padding: "4px 10px",
      borderRadius: RADIUS.full,
      border: `1px solid ${active ? T.accent || T.navy : T.border}`,
      background: active
        ? isDark
          ? "rgba(37,99,235,0.15)"
          : "rgba(37,99,235,0.08)"
        : "transparent",
      color: active ? T.accent || T.navy : T.muted,
      cursor: "pointer",
      fontSize: 11,
      fontWeight: active ? 600 : 400,
      transition: "all 0.15s ease",
    }),
    [T, isDark]
  );

  const totalOk = Math.round(total) === 100;

  return (
    <div>
      {/* ── Header + Profile Selector ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: T.text, margin: 0 }}>
            Simulador de Alocação
          </h2>
          <p style={{ fontSize: 12, color: T.muted, margin: "4px 0 0" }}>
            Markowitz Mean-Variance · Sharpe Optimization · Mercado BR (2015-2024)
          </p>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {Object.keys(PROFILE_WEIGHTS).map((p) => (
            <button
              key={p}
              onClick={() => applyProfile(p)}
              style={btnStyle(profile === p)}
              aria-pressed={profile === p}
              aria-label={`Perfil ${p}`}
            >
              <span>{PROFILE_ICONS[p]}</span>
              <span>{p}</span>
            </button>
          ))}
          {profile === "Personalizado" && (
            <span
              style={{
                padding: "6px 12px",
                borderRadius: RADIUS.sm,
                background: isDark ? "rgba(251,191,36,0.12)" : "#FEF3C7",
                color: T.warning || "#B45309",
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              ✏️ Personalizado
            </span>
          )}
        </div>
      </div>

      {/* ── Profile Description ── */}
      {profile !== "Personalizado" && PROFILE_DESC[profile] && (
        <div
          style={{
            fontSize: 12,
            color: T.muted,
            marginBottom: 16,
            padding: "10px 16px",
            borderRadius: RADIUS.sm,
            background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.02)",
            borderLeft: `3px solid ${T.accent || T.navy}`,
          }}
        >
          {PROFILE_ICONS[profile]} {PROFILE_DESC[profile]}
        </div>
      )}

      {/* ── KPI Metrics Row ── */}
      <Card T={T} style={{ marginBottom: 20, padding: "20px 24px" }}>
        <div
          style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "space-around" }}
          role="status"
          aria-live="polite"
        >
          {metricBox(
            "Retorno Esperado",
            `${metrics.expRet.toFixed(1)}%`,
            T.success || "#16A34A",
            "Nominal bruto a.a.",
            "📈"
          )}
          {metricBox(
            "Volatilidade",
            `${metrics.vol.toFixed(1)}%`,
            T.warning || "#D97706",
            "Desvio padrão anual",
            "📊"
          )}
          {metricBox(
            "Sharpe Ratio",
            metrics.sharpe.toFixed(2),
            metrics.sharpe >= 0.5
              ? T.success || "#16A34A"
              : metrics.sharpe >= 0
                ? T.warning || "#D97706"
                : T.danger || "#DC2626",
            `Rf = ${RISK_FREE}% (Selic)`,
            metrics.sharpe >= 0.5 ? "⭐" : "📐"
          )}
          {metricBox(
            "VaR 95% (1 ano)",
            `${metrics.var95.toFixed(1)}%`,
            metrics.var95 < 0 ? T.danger || "#DC2626" : T.success || "#16A34A",
            "Perda máx. provável",
            "🎯"
          )}
          {metricBox(
            "Diversificação",
            `${metrics.divRatio.toFixed(2)}x`,
            metrics.divRatio > 1.3 ? T.success || "#16A34A" : T.text,
            metrics.divRatio > 1.3 ? "Bem diversificado" : "Baixa diversificação",
            "🔀"
          )}
          {metricBox(
            "Patrimônio 10a",
            fmtBRLCompact(projection[PROJECTION_YEARS]?.esperado),
            T.accent || T.navy,
            `De ${fmtBRLCompact(patrimonio)}`,
            "💰"
          )}
        </div>
      </Card>

      {/* ── Main Grid: Sliders + Charts ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(320px, 1fr) minmax(320px, 1.2fr)",
          gap: 20,
          marginBottom: 20,
        }}
      >
        {/* ── Left: Allocation Sliders ── */}
        <Card T={T} style={{ padding: 20, overflow: "auto", maxHeight: 680 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <STitle
              title="Alocação por Classe"
              sub={totalOk ? "Total: 100% ✓" : `Total: ${total.toFixed(0)}% — ajuste para 100%`}
              T={T}
            />
            {!totalOk && total > 0 && (
              <button
                onClick={normalizeWeights}
                style={{
                  padding: "5px 10px",
                  borderRadius: RADIUS.sm,
                  border: `1px solid ${T.accent || T.navy}`,
                  background: "transparent",
                  color: T.accent || T.navy,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
                aria-label="Normalizar pesos para 100%"
              >
                Normalizar
              </button>
            )}
          </div>

          {!totalOk && total > 0 && (
            <div
              style={{
                fontSize: 11,
                color: T.danger || "#DC2626",
                fontWeight: 600,
                marginBottom: 10,
                padding: "6px 10px",
                borderRadius: RADIUS.sm,
                background: T.dangerBg || "#FEE2E2",
              }}
            >
              ⚠ Total {total.toFixed(1)}% — os cálculos assumem normalização implícita
            </div>
          )}

          {/* Patrimônio input */}
          <div style={{ marginBottom: 18 }}>
            <label
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: T.sub,
                display: "block",
                marginBottom: 4,
              }}
            >
              Patrimônio Simulado
            </label>
            <div style={{ position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: 12,
                  color: T.muted,
                  fontWeight: 600,
                  pointerEvents: "none",
                }}
              >
                R$
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={patrimonio.toLocaleString("pt-BR")}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, "");
                  setPatrimonio(Number(raw) || 0);
                }}
                aria-label="Patrimônio total para simulação"
                style={{
                  width: "100%",
                  padding: "8px 12px 8px 32px",
                  borderRadius: RADIUS.sm,
                  border: `1px solid ${T.border}`,
                  fontSize: 14,
                  fontWeight: 600,
                  color: T.text,
                  background: T.bg,
                  boxSizing: "border-box",
                  fontVariantNumeric: "tabular-nums",
                }}
              />
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {ASSET_CLASSES.map((a) => {
              const w = weights[a.id] || 0;
              const valor = (patrimonio * w) / 100;
              const color = getColor(a.id);
              return (
                <div
                  key={a.id}
                  style={{
                    padding: "8px 10px",
                    borderRadius: RADIUS.sm,
                    background:
                      w > 0
                        ? isDark
                          ? "rgba(255,255,255,0.06)"
                          : "rgba(0,0,0,0.015)"
                        : "transparent",
                    transition: "background 0.2s",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 4,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: color,
                          display: "inline-block",
                          flexShrink: 0,
                          boxShadow: w > 0 ? `0 0 6px ${color}40` : "none",
                        }}
                      />
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: T.text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {a.short}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                      <span
                        style={{
                          fontSize: 11,
                          color: T.muted,
                          minWidth: 65,
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmtBRLCompact(valor)}
                      </span>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={w}
                        onChange={(e) => setWeight(a.id, Number(e.target.value))}
                        aria-label={`Peso ${a.label}`}
                        style={{
                          width: 44,
                          padding: "3px 4px",
                          borderRadius: 6,
                          border: `1px solid ${T.border}`,
                          fontSize: 12,
                          fontWeight: 600,
                          textAlign: "center",
                          background: T.bg,
                          color: T.text,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      />
                      <span style={{ fontSize: 11, color: T.muted, width: 12 }}>%</span>
                    </div>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={w}
                    onChange={(e) => setWeight(a.id, Number(e.target.value))}
                    aria-label={`Slider ${a.label}`}
                    style={{ width: "100%", accentColor: color, height: 4, cursor: "pointer" }}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 10,
                      color: T.muted,
                      marginTop: 2,
                    }}
                  >
                    <span>{a.desc}</span>
                    <span
                      style={{
                        fontVariantNumeric: "tabular-nums",
                        whiteSpace: "nowrap",
                        marginLeft: 8,
                      }}
                    >
                      {a.ret}% · σ {a.vol}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* ── Right: Charts Stack ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Pie Chart */}
          <Card T={T} style={{ padding: 20 }}>
            <STitle title="Composição do Portfólio" T={T} />
            {pieData.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: T.muted, fontSize: 13 }}>
                Ajuste os pesos para visualizar a composição
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <ResponsiveContainer width="55%" height={220}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={85}
                      innerRadius={48}
                      paddingAngle={2}
                      strokeWidth={0}
                    >
                      {pieData.map((d, i) => (
                        <Cell key={i} fill={d.cor} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v, name) => [
                        `${v}% — ${fmtBRLCompact((patrimonio * v) / 100)}`,
                        name,
                      ]}
                      contentStyle={{
                        background: T.card,
                        border: `1px solid ${T.border}`,
                        borderRadius: 8,
                        fontSize: 11,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Legend */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                  {pieData.map((d) => (
                    <div
                      key={d.name}
                      style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 2,
                          background: d.cor,
                          display: "inline-block",
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          color: T.text,
                          fontWeight: 500,
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {d.name}
                      </span>
                      <span
                        style={{
                          color: T.muted,
                          fontWeight: 600,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {d.value}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Efficient Frontier */}
          <Card T={T} style={{ padding: 20 }}>
            <STitle
              title="Fronteira Eficiente"
              sub="Risco × Retorno · Portfólio vs. fronteira de Markowitz"
              T={T}
            />
            <ResponsiveContainer width="100%" height={240}>
              <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={isDark ? "rgba(255,255,255,0.12)" : T.border}
                />
                <XAxis
                  dataKey="vol"
                  name="Volatilidade"
                  unit="%"
                  tick={{ fontSize: 10, fill: T.muted }}
                  label={{
                    value: "Volatilidade (%)",
                    position: "bottom",
                    offset: 0,
                    fontSize: 10,
                    fill: T.muted,
                  }}
                />
                <YAxis
                  dataKey="ret"
                  name="Retorno"
                  unit="%"
                  tick={{ fontSize: 10, fill: T.muted }}
                  label={{
                    value: "Retorno (%)",
                    angle: -90,
                    position: "insideLeft",
                    fontSize: 10,
                    fill: T.muted,
                  }}
                  domain={[8, "auto"]}
                />
                <ReferenceLine
                  y={RISK_FREE}
                  stroke={T.muted}
                  strokeDasharray="5 5"
                  label={{
                    value: `Selic ${RISK_FREE}%`,
                    position: "right",
                    fontSize: 9,
                    fill: T.muted,
                  }}
                />
                {/* Frontier curve */}
                <Scatter
                  name="Fronteira"
                  data={frontier}
                  fill={isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.12)"}
                  r={2}
                  line={{
                    stroke: isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.15)",
                    strokeWidth: 1.5,
                  }}
                />
                {/* Individual asset classes */}
                <Scatter
                  name="Classes"
                  data={assetPoints}
                  r={5}
                  stroke={T.muted}
                  strokeWidth={1.5}
                  fill={isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.05)"}
                />
                {/* Current portfolio — prominent dot */}
                <Scatter
                  name="Seu Portfólio"
                  data={[portfolioPoint]}
                  fill={T.accent || "#2563EB"}
                  r={9}
                  strokeWidth={2.5}
                  stroke={isDark ? "#0F172A" : "#fff"}
                />
                <Tooltip
                  contentStyle={{
                    background: T.card,
                    border: `1px solid ${T.border}`,
                    borderRadius: 8,
                    fontSize: 11,
                  }}
                  formatter={(v, name) => [`${v}%`, name]}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </Card>
        </div>
      </div>

      {/* ── Wealth Projection (Area Chart) ── */}
      <Card T={T} style={{ marginBottom: 20, padding: 20 }}>
        <STitle
          title="Projeção Patrimonial"
          sub={`Evolução estimada do patrimônio em ${PROJECTION_YEARS} anos · Cenários otimista, esperado e pessimista`}
          T={T}
        />
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={projection} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
            <defs>
              <linearGradient id="gradOpt" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={T.success || "#16A34A"} stopOpacity={0.15} />
                <stop offset="100%" stopColor={T.success || "#16A34A"} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradEsp" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={T.accent || "#2563EB"} stopOpacity={0.2} />
                <stop offset="100%" stopColor={T.accent || "#2563EB"} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={isDark ? "rgba(255,255,255,0.12)" : T.border}
            />
            <XAxis
              dataKey="year"
              tick={{ fontSize: 10, fill: T.muted }}
              label={{ value: "Anos", position: "bottom", fontSize: 10, fill: T.muted }}
            />
            <YAxis
              tick={{ fontSize: 10, fill: T.muted }}
              tickFormatter={(v) => fmtBRLCompact(v)}
              width={65}
            />
            <Tooltip
              formatter={(v) => [fmtBRL(v), ""]}
              labelFormatter={(y) => `Ano ${y}`}
              contentStyle={{
                background: T.card,
                border: `1px solid ${T.border}`,
                borderRadius: 8,
                fontSize: 11,
              }}
            />
            <Area
              type="monotone"
              dataKey="otimista"
              name="Otimista (+0.8σ)"
              stroke={T.success || "#16A34A"}
              fill="url(#gradOpt)"
              strokeWidth={1.5}
              strokeDasharray="4 2"
              dot={false}
            />
            <Area
              type="monotone"
              dataKey="esperado"
              name="Esperado"
              stroke={T.accent || "#2563EB"}
              fill="url(#gradEsp)"
              strokeWidth={2}
              dot={false}
            />
            <Area
              type="monotone"
              dataKey="pessimista"
              name="Pessimista (-0.8σ)"
              stroke={T.danger || "#DC2626"}
              fill="none"
              strokeWidth={1.5}
              strokeDasharray="4 2"
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      {/* ── Contribution Breakdown (dual bars: return + risk) ── */}
      <Card T={T} style={{ marginBottom: 20, padding: 20 }}>
        <STitle
          title="Contribuição por Classe"
          sub="Decomposição da contribuição ao retorno e ao risco (Euler) do portfólio"
          T={T}
        />
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={barData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={isDark ? "rgba(255,255,255,0.12)" : T.border}
            />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: T.muted }} />
            <YAxis tick={{ fontSize: 10, fill: T.muted }} unit="%" />
            <Tooltip
              contentStyle={{
                background: T.card,
                border: `1px solid ${T.border}`,
                borderRadius: 8,
                fontSize: 11,
              }}
              formatter={(v, name) => [`${v}%`, name]}
            />
            <Bar dataKey="retorno" name="Contribuição Retorno" radius={[4, 4, 0, 0]}>
              {barData.map((d, i) => (
                <Cell key={i} fill={d.cor} />
              ))}
              <LabelList
                dataKey="retorno"
                position="top"
                style={{ fontSize: 9, fill: T.muted }}
                formatter={(v) => `${v}%`}
              />
            </Bar>
            <Bar dataKey="risco" name="Contribuição Risco" radius={[4, 4, 0, 0]} fillOpacity={0.4}>
              {barData.map((d, i) => (
                <Cell key={i} fill={d.cor} />
              ))}
            </Bar>
            <Legend wrapperStyle={{ fontSize: 10, color: T.muted }} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* ── Profile Comparison Table ── */}
      <Card T={T} style={{ marginBottom: 20, padding: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <STitle
            title="Comparação de Perfis"
            sub="Métricas de cada perfil pré-configurado vs. seu portfólio"
            T={T}
          />
          <button
            onClick={() => setShowCompare(!showCompare)}
            style={chipStyle(showCompare)}
            aria-pressed={showCompare}
            aria-label={showCompare ? "Ocultar comparação" : "Exibir comparação"}
          >
            {showCompare ? "Ocultar" : "Exibir"}
          </button>
        </div>
        {showCompare && (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
              aria-label="Comparação de perfis de investimento"
            >
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                  <th
                    scope="col"
                    style={{
                      textAlign: "left",
                      padding: "8px 12px",
                      color: T.sub,
                      fontWeight: 600,
                    }}
                  >
                    Perfil
                  </th>
                  <th
                    scope="col"
                    style={{
                      textAlign: "right",
                      padding: "8px 12px",
                      color: T.sub,
                      fontWeight: 600,
                    }}
                  >
                    Retorno
                  </th>
                  <th
                    scope="col"
                    style={{
                      textAlign: "right",
                      padding: "8px 12px",
                      color: T.sub,
                      fontWeight: 600,
                    }}
                  >
                    Volatilidade
                  </th>
                  <th
                    scope="col"
                    style={{
                      textAlign: "right",
                      padding: "8px 12px",
                      color: T.sub,
                      fontWeight: 600,
                    }}
                  >
                    Sharpe
                  </th>
                  <th
                    scope="col"
                    style={{
                      textAlign: "right",
                      padding: "8px 12px",
                      color: T.sub,
                      fontWeight: 600,
                    }}
                  >
                    VaR 95%
                  </th>
                  <th
                    scope="col"
                    style={{
                      textAlign: "right",
                      padding: "8px 12px",
                      color: T.sub,
                      fontWeight: 600,
                    }}
                  >
                    Max DD Est.
                  </th>
                  <th
                    scope="col"
                    style={{
                      textAlign: "right",
                      padding: "8px 12px",
                      color: T.sub,
                      fontWeight: 600,
                    }}
                  >
                    Diversif.
                  </th>
                  <th
                    scope="col"
                    style={{
                      textAlign: "right",
                      padding: "8px 12px",
                      color: T.sub,
                      fontWeight: 600,
                    }}
                  >
                    Patrimônio 10a
                  </th>
                </tr>
              </thead>
              <tbody>
                {profileComparison.map((p) => {
                  const proj10 = Math.round(patrimonio * Math.pow(1 + p.expRet / 100, 10));
                  const isActive = p.name === profile;
                  return (
                    <tr
                      key={p.name}
                      style={{
                        borderBottom: `1px solid ${T.border}`,
                        background: isActive
                          ? isDark
                            ? "rgba(37,99,235,0.08)"
                            : "rgba(37,99,235,0.04)"
                          : "transparent",
                        cursor: "pointer",
                      }}
                      onClick={() => applyProfile(p.name)}
                      aria-label={`Selecionar perfil ${p.name}`}
                    >
                      <td
                        style={{
                          padding: "10px 12px",
                          fontWeight: isActive ? 700 : 500,
                          color: T.text,
                        }}
                      >
                        {p.icon} {p.name}
                      </td>
                      <td
                        style={{
                          padding: "10px 12px",
                          textAlign: "right",
                          color: T.success || "#16A34A",
                          fontWeight: 600,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {p.expRet.toFixed(1)}%
                      </td>
                      <td
                        style={{
                          padding: "10px 12px",
                          textAlign: "right",
                          color: T.warning || "#D97706",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {p.vol.toFixed(1)}%
                      </td>
                      <td
                        style={{
                          padding: "10px 12px",
                          textAlign: "right",
                          fontWeight: 600,
                          fontVariantNumeric: "tabular-nums",
                          color: p.sharpe >= 0.5 ? T.success || "#16A34A" : T.text,
                        }}
                      >
                        {p.sharpe.toFixed(2)}
                      </td>
                      <td
                        style={{
                          padding: "10px 12px",
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                          color: p.var95 < 0 ? T.danger || "#DC2626" : T.text,
                        }}
                      >
                        {p.var95.toFixed(1)}%
                      </td>
                      <td
                        style={{
                          padding: "10px 12px",
                          textAlign: "right",
                          color: T.danger || "#DC2626",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {p.maxDD.toFixed(1)}%
                      </td>
                      <td
                        style={{
                          padding: "10px 12px",
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                          color: p.divRatio > 1.3 ? T.success || "#16A34A" : T.text,
                        }}
                      >
                        {p.divRatio.toFixed(2)}x
                      </td>
                      <td
                        style={{
                          padding: "10px 12px",
                          textAlign: "right",
                          fontWeight: 600,
                          color: T.accent || T.navy,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmtBRLCompact(proj10)}
                      </td>
                    </tr>
                  );
                })}
                {/* Current portfolio row (if custom) */}
                {profile === "Personalizado" && (
                  <tr
                    style={{
                      borderBottom: `1px solid ${T.border}`,
                      background: isDark ? "rgba(251,191,36,0.06)" : "rgba(251,191,36,0.08)",
                    }}
                  >
                    <td style={{ padding: "10px 12px", fontWeight: 700, color: T.text }}>
                      ✏️ Seu Portfólio
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        color: T.success || "#16A34A",
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {metrics.expRet.toFixed(1)}%
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        color: T.warning || "#D97706",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {metrics.vol.toFixed(1)}%
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                        color: metrics.sharpe >= 0.5 ? T.success || "#16A34A" : T.text,
                      }}
                    >
                      {metrics.sharpe.toFixed(2)}
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                        color: metrics.var95 < 0 ? T.danger || "#DC2626" : T.text,
                      }}
                    >
                      {metrics.var95.toFixed(1)}%
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        color: T.danger || "#DC2626",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {metrics.maxDD.toFixed(1)}%
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                        color: metrics.divRatio > 1.3 ? T.success || "#16A34A" : T.text,
                      }}
                    >
                      {metrics.divRatio.toFixed(2)}x
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontWeight: 600,
                        color: T.accent || T.navy,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {fmtBRLCompact(projection[PROJECTION_YEARS]?.esperado)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Literature Reference ── */}
      <Card T={T} style={{ padding: "16px 20px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.sub, marginBottom: 10 }}>
          Referências Teóricas
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "6px 24px",
            fontSize: 11,
            color: T.muted,
            lineHeight: 1.6,
          }}
        >
          {[
            [
              "Markowitz (1952)",
              "Portfolio Selection, Journal of Finance. Otimização média-variância.",
            ],
            ["Sharpe (1964)", "Capital Asset Pricing Model. Índice Sharpe = (Rp - Rf) / σp."],
            [
              "Black-Litterman (1992)",
              "Global Portfolio Optimization. Views do investidor sobre retornos.",
            ],
            ["Swensen (2000)", "Pioneering Portfolio Management. Modelo Yale de endowment."],
            ["ANBIMA", "Classificação de perfil do investidor. Conservador → Agressivo."],
            ["CVM 30 (2021)", "Normas de suitability para assessores de investimento."],
            ["Parâmetros", "Retorno e volatilidade calibrados com dados ANBIMA/B3 (2015-2024)."],
            [
              "Correlações",
              "Matriz rolling 36m de benchmarks BR (CDI, IMA-B, IBOV, S&P BRL, IFIX).",
            ],
          ].map(([title, desc], i) => (
            <div key={i}>
              <strong style={{ color: isDark ? T.text : T.sub }}>{title}</strong> — {desc}
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: 10,
            padding: "8px 12px",
            borderRadius: RADIUS.sm,
            background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.02)",
            fontSize: 10,
            color: T.muted,
          }}
        >
          ⚠ Este simulador é uma ferramenta educacional. Retornos passados não garantem retornos
          futuros. Consulte um assessor CVM antes de investir.
        </div>
      </Card>
    </div>
  );
}

AdminSimuladorAlocacaoTab.propTypes = {
  T: PropTypes.object.isRequired,
};
